import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {LoanTile} from '../tiles/tiles';
import {TileFactory} from '../tiles/tile-factory';
import {LoanDetailsService} from '../../../../service/loan-details.service';
import {LoansAndApplicationsSegregator} from '../loans-and-applications-segregator';
import {Loan} from '../../../../model/loan.model';
import {Observable} from 'rxjs';
import {MatTableDataSource} from '@angular/material/table';
import {DisplayableLoanStatus} from '../../../../model/loan-status.model';
import {ChannelPartnerService} from '../../../../service/channel-partner.service';
import {LoanSubventionDetails} from '../../../../model/subvention-preference.model';
import {MathUtil} from '../../../../utils/math.util';
import {SubventionUtil} from '../../../../utils/subvention-util';
import {CommonUtil} from '../../../../utils/common.util';
import {ChannelPartner, ChannelPartnerGroupType} from '../../../../model/channel-partner.model';

@Component({
    selector: 'app-all-loans',
    templateUrl: './all-loans.component.html',
    styleUrls: ['./all-loans.component.scss', '../../../../../assets/styles/loan-tiles.scss']
})
export class AllLoansComponent implements OnInit, OnDestroy {
    @Input()
    userId: string;
    loanTiles: LoanTile[];

    loanTilesObservables: Observable<any>;
    dataSource: MatTableDataSource<LoanTile>;
    defaultPageSize = 5;

    constructor(private loanDetailsService: LoanDetailsService, private segregator: LoansAndApplicationsSegregator,
                private channelPartnerService: ChannelPartnerService,
                private subventionUtil: SubventionUtil, private tileFactory: TileFactory) {
        this.dataSource =  new MatTableDataSource<LoanTile>();
    }

    async ngOnInit(): Promise<void> {
        const allLoans = await this.loanDetailsService.getLoansByUserId(this.userId);

        const acceptedButYetToBeDisbursedLoans = this.segregator.filterAcceptedButYetToBeDisbursedApplications(allLoans);
        const activeLoans = this.segregator.filterActiveLoans(allLoans);
        const repaidLoans = this.segregator.filterCompletelyRepaidLoans(allLoans);

        const partners = await this.getPartners(allLoans);
        const partnersWithPartnerGroupsMapping = await this.setIfPartnerBelongsToSubventedPreApprovalPartnerGroup(partners);

        if (acceptedButYetToBeDisbursedLoans || activeLoans || repaidLoans) {

            const loansSubventionDetails = await this.subventionUtil.getSubventionDetailsForLoans(allLoans);

            this.loanTiles = [];
            if (acceptedButYetToBeDisbursedLoans && acceptedButYetToBeDisbursedLoans.length > 0) {
                const tiles = (await this.constructTilesForLoans(acceptedButYetToBeDisbursedLoans, loansSubventionDetails, partnersWithPartnerGroupsMapping))
                    .sort((firstLoanTile, secondLoanTile) =>
                    new Date(secondLoanTile.applicationDate).getTime() - new Date(firstLoanTile.applicationDate).getTime());
                this.loanTiles.push(...tiles);
            } if (activeLoans && activeLoans.length > 0) {
                const tiles = (await this.constructTilesForLoans(activeLoans, loansSubventionDetails, partnersWithPartnerGroupsMapping))
                    .sort((firstLoanTile, secondLoanTile) =>
                    new Date(secondLoanTile.applicationDate).getTime() - new Date(firstLoanTile.applicationDate).getTime());
                this.loanTiles.push(...tiles);
            } if (repaidLoans && repaidLoans.length > 0) {
                const tiles = (await this.constructTilesForLoans(repaidLoans, loansSubventionDetails, partnersWithPartnerGroupsMapping))
                    .sort((firstLoanTile, secondLoanTile) =>
                    new Date(secondLoanTile.applicationDate).getTime() - new Date(firstLoanTile.applicationDate).getTime());
                this.loanTiles.push(...tiles);
            }
        } else {
            this.loanTiles = null;
        }
        this.dataSource.data = this.loanTiles;
        this.loanTilesObservables = this.dataSource.connect();
    }

    ngOnDestroy() {
        if (this.dataSource) {
            this.dataSource.disconnect();
        }
    }

    isActiveLoan(loanTile: LoanTile): boolean {
        return loanTile.status === DisplayableLoanStatus.InProgress || loanTile.status === DisplayableLoanStatus.Active;
    }

    private async constructTilesForLoans(loans: Loan[], loansSubventionDetailsForAllLoans: LoanSubventionDetails[],
                                         partnersWithPartnerGroupsMapping: Map<string, boolean>): Promise<LoanTile[]> {
        if (loans.length > 0) {

            const loansSubventionDetails = loansSubventionDetailsForAllLoans.filter(loansSubventionDetailsForAllLoan =>
                loans.find(loan => loan._id === loansSubventionDetailsForAllLoan.loanId));

            for (const loanSubventionDetail of loansSubventionDetails) {
                const loan = loans.find(loanDetails => loanDetails._id === loanSubventionDetail.loanId);
                loan.effective_roi = MathUtil.roundOff(loanSubventionDetail.effectiveRoi);
            }
            return loans.map(loan => {
                let noRedirection = false;
                if (!CommonUtil.isEmpty(loan.partner)) {
                    const isPreApprovalSubventionPartner = partnersWithPartnerGroupsMapping.get(loan.partner);
                    if (!CommonUtil.isEmpty(isPreApprovalSubventionPartner) && isPreApprovalSubventionPartner === true) {
                        loan.loan_tenure = null;
                        noRedirection = true;
                    }
                }
                return this.tileFactory.createForLoan(loan, null, noRedirection);
            });
        } else {
            return [];
        }
    }

    private async getPartners(loans: Loan[]): Promise<ChannelPartner[]> {
        const partnerNames = Array.from(new Set<string>(loans.filter(loan => !CommonUtil.isEmpty(loan.partner)).map(loan => loan.partner)));
        return partnerNames && partnerNames.length > 0 ? await this.channelPartnerService.getPartnerByNames(partnerNames) : [];
    }

    private async setIfPartnerBelongsToSubventedPreApprovalPartnerGroup(partners: ChannelPartner[]): Promise<Map<string, boolean>> {
        const map = new Map<string, boolean>();
        if (partners.length > 0) {
            const partnerGroup = await this.channelPartnerService.getChannelPartnerGroupByType(ChannelPartnerGroupType.SubventedPreApproval);
            partners.forEach(partner => {
                if (partner.partnerGroupIds && partner.partnerGroupIds.includes(partnerGroup._id)) {
                    map.set(partner.partnerName, true);
                } else {
                    map.set(partner.partnerName, false);
                }
            });
        }
        return map;
    }
}
